import { request } from "react-rb/request"

/**
 * @typedef CommandPaletteSubscriber
 * @type {object}
 * @property {number} id
 * @property {string} email_address
 * @property {?string} first_name
 */

/**
 * Search subscribers by email for the command palette.
 *
 * @param {object} params
 * @param {string} params.query Partial email term.
 * @returns {Promise<{ subscribers: CommandPaletteSubscriber[] }>}
 */
export async function searchSubscribers({ query }) {
  const params = new URLSearchParams({ q: query })

  const response = await request(`/command_palette/subscribers?${params}`, {
    method: "GET",
    throwOnError: true
  })

  return response.data
}

/**
 * @typedef CommandPaletteTag
 * @type {object}
 * @property {number} id
 * @property {string} name
 */

/**
 * Fetch the account's recent tags for the command palette. `has_more` is true
 * when the account has more tags than the server returns here, meaning the
 * client doesn't hold the full list and must search server-side (via
 * `searchTags`) instead of filtering this list client-side.
 *
 * @returns {Promise<{ tags: CommandPaletteTag[], has_more: boolean }>}
 */
export async function fetchTags() {
  const response = await request("/command_palette/tags", {
    method: "GET",
    throwOnError: true
  })

  return response.data
}

/**
 * Search the account's tags by name for the command palette. Used for accounts
 * with more tags than the preload returns, where client-side filtering would
 * miss matches outside the preloaded list.
 *
 * @param {object} params
 * @param {string} params.query Partial tag name.
 * @returns {Promise<{ tags: CommandPaletteTag[] }>}
 */
export async function searchTags({ query }) {
  const params = new URLSearchParams({ q: query })

  const response = await request(`/command_palette/tags?${params}`, {
    method: "GET",
    throwOnError: true
  })

  return response.data
}

/**
 * @typedef CommandPaletteSegment
 * @type {object}
 * @property {number} id
 * @property {string} name
 */

/**
 * Fetch the account's full segment list for the command palette. The palette
 * filters it client-side, so it's fetched once per subpage visit rather than
 * per keystroke.
 *
 * @returns {Promise<{ segments: CommandPaletteSegment[] }>}
 */
export async function fetchSegments() {
  const response = await request("/command_palette/segments", {
    method: "GET",
    throwOnError: true
  })

  return response.data
}

/**
 * @typedef CommandPaletteBroadcast
 * @type {object}
 * @property {number} id
 * @property {string} subject
 * @property {string} url
 */

/**
 * Search broadcasts for the command palette. Matches subject, internal note,
 * and post title/description (reuses the campaigns index search).
 *
 * @param {object} params
 * @param {string} params.query Partial search term.
 * @returns {Promise<{ broadcasts: CommandPaletteBroadcast[] }>}
 */
export async function searchBroadcasts({ query }) {
  const params = new URLSearchParams({ q: query })

  const response = await request(`/command_palette/broadcasts?${params}`, {
    method: "GET",
    throwOnError: true
  })

  return response.data
}

/**
 * @typedef CommandPaletteForm
 * @type {object}
 * @property {number} id
 * @property {string} name
 * @property {"form"|"landing_page"} type
 * @property {string} url
 */

/**
 * Search the account's forms and landing pages by name for the command palette.
 * Covers forms and both landing page versions (all Forms::Container); legacy
 * `LandingPage` records are not searchable.
 *
 * @param {object} params
 * @param {string} params.query Partial name.
 * @returns {Promise<{ forms: CommandPaletteForm[] }>}
 */
export async function searchForms({ query }) {
  const params = new URLSearchParams({ q: query })

  const response = await request(`/command_palette/forms?${params}`, {
    method: "GET",
    throwOnError: true
  })

  return response.data
}

/**
 * @typedef CommandPaletteSequence
 * @type {object}
 * @property {number} id
 * @property {string} name
 */

/**
 * Fetch the account's full sequence list for the command palette. The palette
 * filters it client-side, so it's fetched once per subpage visit rather than
 * per keystroke.
 *
 * @returns {Promise<{ sequences: CommandPaletteSequence[] }>}
 */
export async function fetchSequences() {
  const response = await request("/command_palette/sequences", {
    method: "GET",
    throwOnError: true
  })

  return response.data
}

/**
 * @typedef CommandPaletteAutomation
 * @type {object}
 * @property {number} id
 * @property {string} name
 */

/**
 * Fetch the account's full visual automation list for the command palette. The
 * palette filters it client-side, so it's fetched once per subpage visit rather
 * than per keystroke.
 *
 * @returns {Promise<{ automations: CommandPaletteAutomation[] }>}
 */
export async function fetchAutomations() {
  const response = await request("/command_palette/automations", {
    method: "GET",
    throwOnError: true
  })

  return response.data
}
